import { createSelector, createSlice } from '@reduxjs/toolkit';
import { get, isEqual, pick } from 'lodash';

import { toFilter } from '~/components/SearchV2/SearchV2.utils';
import { SliceType } from '~/enums/SliceType';
import {
  SearchDocumentItem,
  SearchQuery,
} from '~/features/advanced-search/types';
import {
  buildFilterQuery,
  resolveLegacyFilterIds,
} from '~/features/advanced-search/utils/build-query';
import { SavedSearch } from '~/features/search/types/search';
import {
  dedupeFilters,
  extractSearchBarText,
  flattenQueryEntities,
  testHasOrOperator,
  testIsBooleanTextSearch,
  testIsQueryV3,
  wrapBareEntitiesInSections,
} from '~/features/search/utils/queryUtils';
import { SearchItem } from '~/redux/api/evisearch/searchTypes';
import { DropdownFieldValue } from '~/types/Field';
import { DEFAULT_TABLE_SORT_COLUMN_ORDER } from '~/utils/table';

import { toSearchV3Filters } from '../../api/methods/searchV3';
import { getPersistedState } from '../../persist';
import { isActiveFilter } from '../../slices/search';
import {
  AskAiHandoff,
  ColumnWidths,
  InitData,
  InitSavedSearch,
  ApplyPrepopulateData,
  KeyMapToPersist,
  SearchV3Slice,
} from './types';

const name = SliceType.SearchV3;

const keysToPersist: (keyof SearchV3Slice)[] = ['columnWidths'];

const keyMapToPersist: KeyMapToPersist = keysToPersist.reduce<KeyMapToPersist>(
  (prev, curr) => {
    prev[curr] = [name, curr];
    return prev;
  },
  {},
);

const persistedState = {
  columnWidths: getPersistedState(keyMapToPersist.columnWidths!, {}),
};

export const initialState: SearchV3Slice = {
  columnOrder: [],
  fieldValues: {},
  intervals: {},
  chartWidths: {},
  page: 1,
  pageSize: 20,
  searchText: undefined,
  queryBuilder: undefined,
  queryBuilderPanelVisible: false,
  queryFields: [],
  searchResultCache: {},
  sortBy: DEFAULT_TABLE_SORT_COLUMN_ORDER,
  selectedFilters: [],
  defaultFilters: [],
  savedSearch: undefined,
  queryFilters: undefined,
  prepopulateQuery: undefined,
  askAiHandoff: undefined,
  initialValues: {
    columns: [],
    queryBuilder: undefined,
    selectedFilters: [],
    queryFilters: [],
    searchText: undefined,
  },
  ...persistedState,
};

export default createSlice({
  name,
  initialState,
  selectors: {
    selectColumnOrder: (state: SearchV3Slice) => state.columnOrder,
    selectSelectedFilters: (state: SearchV3Slice) => state.selectedFilters,
    selectActiveFilters: createSelector(
      (state: SearchV3Slice) => state.selectedFilters,
      (selectedFilters) => selectedFilters.filter(isActiveFilter),
    ),
    selectFeaturedClauses: createSelector(
      (state: SearchV3Slice) => state.selectedFilters,
      (selectedFilters) =>
        selectedFilters
          .filter(isActiveFilter)
          .filter(
            (filter) =>
              (filter.fieldId === 'clause' ||
                filter.fieldId === 'clause_Document') &&
              filter.operatorId === 'contains_any',
          )
          .map(
            (clauseFilter) => (clauseFilter.values[0] as any)?.provision ?? '',
          ),
    ),
    selectIsSearchDirty: (state: SearchV3Slice) => {
      if (!state.initialValues) return false;

      return !isEqual(
        {
          columns: state.initialValues.columns,
          queryBuilder: state.initialValues.queryBuilder,
          selectedFilters: state.initialValues.selectedFilters,
          searchText: state.initialValues.searchText,
        },
        {
          columns: state.columnOrder?.map(String),
          queryBuilder: state.queryBuilder,
          selectedFilters: state.selectedFilters,
          searchText: state.searchText!,
        },
      );
    },
    selectColumnWidths: (state: SearchV3Slice) => state.columnWidths,
    selectSortBy: (state: SearchV3Slice) => state.sortBy,
    selectFieldValues: (state: SearchV3Slice) => state.fieldValues,
    selectResultsCache: (state: SearchV3Slice) => state.searchResultCache,
    selectPage: (state: SearchV3Slice) => state.page,
    selectPageSize: (state: SearchV3Slice) => state.pageSize,
    selectSearchText: (state: SearchV3Slice) => state.searchText,
    selectQueryBuilder: (state: SearchV3Slice) => state.queryBuilder,
    selectQueryFilters: (state: SearchV3Slice) => state.queryFilters,
    selectQueryBuilderPanelVisible: (state: SearchV3Slice) =>
      state.queryBuilderPanelVisible,
    selectQueryFields: (state: SearchV3Slice) => state.queryFields,
    selectDefaultFilters: (state: SearchV3Slice) => state.defaultFilters,
    selectIsInitialized: (state: SearchV3Slice) =>
      state.searchText !== undefined,
    selectSavedSearch: (state: SearchV3Slice) => state.savedSearch,
    selectInitialValues: (state: SearchV3Slice) => state.initialValues,
    selectPrepopulateQuery: (state: SearchV3Slice) => state.prepopulateQuery,
    selectAskAiHandoff: (state: SearchV3Slice) => state.askAiHandoff,
  },
  reducers: {
    init: (state, action: { payload: InitData }) => {
      state.columnOrder = action.payload.columnOrder;
      state.pageSize = action.payload.pageSize;
      state.sortBy = action.payload.sortBy;

      state.initialValues = {
        columns: action.payload.columnOrder,
        queryBuilder: undefined,
        queryFilters: buildFilterQuery({
          filters: action.payload.selectedFilters ?? [],
        }),
        selectedFilters: action.payload.selectedFilters ?? [],
        searchText: undefined,
      };
    },
    initSavedSearchData: (state, action: { payload: InitSavedSearch }) => {
      const { searchFilters } = action.payload;
      const savedSearch = action.payload.savedSearch ?? state.savedSearch;

      if (savedSearch && testIsQueryV3(savedSearch)) {
        state.columnOrder = savedSearch.resultTable.map((column) =>
          column.filterId.toString(),
        );
        if (savedSearch.isComplexQuery) {
          state.queryBuilder = savedSearch.query;
          state.queryFilters = savedSearch.query;
        } else {
          state.selectedFilters = [
            ...toSearchV3Filters(savedSearch.query, searchFilters),
            ...(savedSearch.filters?.map((filter) => toFilter(filter)) ?? []),
          ];
          state.queryFilters = buildFilterQuery({
            filters: state.selectedFilters ?? [],
            searchFilters,
          });
        }

        state.initialValues = {
          columns: state.columnOrder,
          queryBuilder: state.queryBuilder,
          selectedFilters: state.selectedFilters,
          queryFilters: state.queryFilters,
          searchText: state.searchText,
        };
      }
    },
    setSavedSearch: (state, action: { payload: SavedSearch | undefined }) => {
      state.savedSearch = action.payload;
    },
    setPrepopulateQuery: (state, { payload }: { payload: SearchQuery }) => {
      // Stash a query (e.g. from Ask AI) for the search page to seed once its
      // filters load; reset existing editable state so pre-population starts clean.
      state.prepopulateQuery = payload;
      // A handoff belongs to the query it arrived with, so a new query drops the old answer.
      state.askAiHandoff = undefined;
      state.queryBuilder = undefined;
      state.selectedFilters = [];
      state.searchText = undefined;
      state.queryFilters = undefined;
    },
    setAskAiHandoff: (state, { payload }: { payload: AskAiHandoff }) => {
      state.askAiHandoff = payload;
    },
    // Dismissing is one-way for the visit. Nothing restores the answer short of asking again.
    clearAskAiHandoff: (state) => {
      state.askAiHandoff = undefined;
    },
    applyPrepopulateData: (
      state,
      { payload }: { payload: ApplyPrepopulateData },
    ) => {
      if (!state.prepopulateQuery) return;

      const { searchFilters } = payload;
      // Ask AI scopes its search with legacy Pilot filter IDs, unknown to this filter set.
      // An untranslated ID would render no chip at all.
      const query = resolveLegacyFilterIds(
        state.prepopulateQuery,
        searchFilters,
      );

      // Chips always AND-combine (see buildQuery in advanced-search/utils/build-query.ts), so a query
      // with an `or` anywhere cannot be flattened without silently turning that `or` into an `and`.
      if (testHasOrOperator(flattenQueryEntities(query))) {
        // The query builder's parser requires every top-level entity to be a section or
        // operator; wrap bare filters so this still opens editable, not read-only.
        const queryBuilderQuery = wrapBareEntitiesInSections(query);
        state.queryBuilder = queryBuilderQuery;
        state.queryFilters = queryBuilderQuery;
        state.selectedFilters = [];
        state.searchText = undefined;
        state.prepopulateQuery = undefined;

        state.initialValues = {
          columns: state.columnOrder,
          queryBuilder: state.queryBuilder,
          selectedFilters: state.selectedFilters,
          queryFilters: state.queryFilters,
          searchText: state.searchText,
        };
        return;
      }

      // Flatten a possibly scope-merged query, route free-text to the main bar,
      // and keep the de-duplicated structured filters as chips.
      const flattened = flattenQueryEntities(query);
      const structuredFilters = dedupeFilters(
        flattened.filter((entity) => !testIsBooleanTextSearch(entity)),
      );

      // Populate the editable filter bar by mirroring the simple saved-search path:
      // convert the query into editable filters and leave `queryBuilder` unset.
      state.searchText = extractSearchBarText(flattened);
      state.selectedFilters = toSearchV3Filters(
        structuredFilters,
        searchFilters,
      );
      state.queryBuilder = undefined;
      state.queryFilters = undefined;
      state.prepopulateQuery = undefined;

      state.initialValues = {
        columns: state.columnOrder,
        queryBuilder: undefined,
        selectedFilters: state.selectedFilters,
        queryFilters: state.queryFilters,
        searchText: state.searchText,
      };
    },
    patchColumnWidths: (state, action: { payload: ColumnWidths }) => {
      state.columnWidths = {
        ...state.columnWidths,
        ...action.payload,
      };
    },
    patchSearchResultsCache: (
      state,
      { payload = [] }: { payload: SearchDocumentItem[] },
    ) => {
      payload.forEach((item) => (state.searchResultCache[item.id] = item));
    },
    patchEvisearchResultsCache: (
      state,
      { payload = [] }: { payload: SearchItem[] },
    ) => {
      payload.forEach(
        (item) =>
          (state.searchResultCache[item.id] = {
            ...item,
            document_handlers: get(
              item,
              'selected_field_values.document_handler_id_Document.value_structured',
              [],
            ),
            full_text_matches: item.highlights?.full_text_matches ?? [],
            // TODO: Map types correctly once the API gets formalized
            permissions: item.permissions as any,
            selected_field_values: item.selected_field_values as any,
            id: item.id as any,
          }),
      );
    },

    setColumnOrder: (state, action) => {
      state.columnOrder = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setFieldValues: (
      state,
      {
        payload,
      }: { payload: { fieldId: string; values: DropdownFieldValue[] } },
    ) => {
      state.fieldValues[payload.fieldId] = payload.values;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setSearchText: (state, { payload }) => {
      state.searchText = payload;
    },
    setQueryBuilder: (state, action) => {
      state.queryBuilder = action.payload;
    },
    setQueryFilters: (state, action) => {
      state.queryFilters = action.payload;
    },
    setQueryBuilderPanelVisible: (state, action) => {
      state.queryBuilderPanelVisible = action.payload;
    },
    setQueryFields: (state, { payload }) => {
      state.queryFields = payload;
    },
    setDefaultFilters: (state, action) => {
      state.defaultFilters = action.payload;
    },
    setSelectedFilters: (state, action) => {
      state.selectedFilters = action.payload;
    },
    setSortBy: (state, action) => {
      const newSortBy = { ...action.payload };
      state.sortBy = newSortBy;
    },
    reset: (state) => {
      return {
        ...initialState,
        ...pick(state, ['selectedFilters', 'searchResultCache']),
        filters: [],
        selectedFilters: [],
      };
    },
  },
});
